import React, { useState } from 'react';
import { Box, Chip, Menu, MenuItem, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { BaseSelectCellProps } from './LeadSelectCell';
import OptionMenuPaper from './OptionMenuPaper';
import { resolveOptionLabel } from '../../../utils/leads/leadsUtils';

export type LeadSingleSelectCellProps = BaseSelectCellProps;

export const LeadSingleSelectCell: React.FC<LeadSingleSelectCellProps> = ({
  value,
  options = [],
  onChange,
  onAddOption,
  onActionClick,
  actionText,
  placeholder = '—',
  menuWidth = 160,
  PaperProps,
  MenuListProps,
  emitStringValue = false,
  sortOptions = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMinWidth, setMenuMinWidth] = useState<number | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const container =
      (event.currentTarget.closest('.cell-dropdown-container') as HTMLElement) || event.currentTarget;
    if (container) {
      setMenuMinWidth(container.offsetWidth);
    }
    setAnchorEl(container as HTMLElement);
  };

  const handleClose = (event?: any) => {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  let processedOptions = [...options];
  if (sortOptions) {
    processedOptions.sort((a, b) => {
      const labelA = typeof a === 'string' ? a : a.label || a.value || String(a);
      const labelB = typeof b === 'string' ? b : b.label || b.value || String(b);
      return labelA.localeCompare(labelB);
    });
  }

  const stringOptions: string[] = processedOptions.map((opt) =>
    typeof opt === 'string' ? opt : (opt as any).label || (opt as any).value || String(opt)
  );

  let normalizedValue = value;
  if (Array.isArray(normalizedValue)) {
    normalizedValue = normalizedValue[0];
  } else if (typeof normalizedValue === 'string' && normalizedValue.trim().startsWith('[') && normalizedValue.trim().endsWith(']')) {
    try {
      const parsed = JSON.parse(normalizedValue);
      if (Array.isArray(parsed)) {
        normalizedValue = parsed[0];
      }
    } catch (e) {}
  }

  let displayLabel: string;
  if (emitStringValue) {
    if (value === undefined || value === null || value === '') {
      displayLabel = placeholder;
    } else {
      const matchOpt = processedOptions.find((o) =>
        typeof o === 'object' && o !== null
          ? (o as any).value === value || (o as any).label === value || String((o as any).value) === String(value)
          : o === value
      );
      if (matchOpt) {
        displayLabel = typeof matchOpt === 'string' ? matchOpt : (matchOpt as any).label || ((matchOpt as any).value !== undefined ? String((matchOpt as any).value) : String(value));
      } else {
        displayLabel = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
      }
    }
  } else {
    displayLabel = resolveOptionLabel(normalizedValue, stringOptions) || (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== '' ? String(normalizedValue) : placeholder);
  }

  const handleSelect = (optIndex: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (emitStringValue) {
      const rawOpt = processedOptions[optIndex];
      const emittedValue = typeof rawOpt === 'string'
        ? rawOpt
        : (rawOpt as any)?.value !== undefined
          ? (rawOpt as any).value
          : (rawOpt as any)?.label || stringOptions[optIndex];
      onChange(emittedValue);
    } else {
      onChange(optIndex);
    }
    handleClose();
  };

  const [justAddedId, setJustAddedId] = useState<number | null>(null);

  return (
    <Box
      className="cell-dropdown-container"
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <Chip
        label={displayLabel}
        variant="outlined"
        color="default"
        deleteIcon={<KeyboardArrowDownIcon style={{ fontSize: '16px' }} />}
        onDelete={handleClick}
        sx={{
          width: '100%',
          height: '30px',
          borderRadius: '100px',
          display: 'inline-flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          fontWeight: 500,
          fontSize: '0.75rem',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: 'primary.main',
          },
          '& .MuiChip-label': {
            paddingRight: '4px',
            paddingLeft: '10px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            flexGrow: 1,
            display: 'block',
            lineHeight: '30px',
            fontSize: '0.75rem',
          },
          '& .MuiChip-deleteIcon': {
            color: 'text.secondary',
            margin: '0 6px 0 2px',
            transition: 'color 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              color: 'text.primary',
            },
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={
          PaperProps || {
            component: OptionMenuPaper,
            onAddOption: onAddOption ? async (newOptLabel: string) => {
              await onAddOption(newOptLabel);
              const newIndex = stringOptions.length;
              setJustAddedId(newIndex);
              onChange(newIndex);
              return newIndex;
            } : undefined,
            onActionClick: onActionClick ? () => {
              handleClose();
              onActionClick();
            } : undefined,
            actionText,
            style: {
              minWidth: menuMinWidth || menuWidth,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            },
          }
        }
        MenuListProps={MenuListProps}
      >
        {stringOptions.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="caption" color="text.secondary">
              No options defined
            </Typography>
          </MenuItem>
        ) : (
          stringOptions.map((optLabel, index) => {
            const rawOpt = processedOptions[index];
            const isSelected = emitStringValue
              ? value === optLabel || value === (rawOpt as any)?.value || value === (rawOpt as any)?.label ||  ((rawOpt as any)?.value !== undefined && String((rawOpt as any).value) === String(value))
              : typeof normalizedValue === 'number' && normalizedValue === index;
            return (
              <MenuItem
                key={`${optLabel}-${index}`}
                selected={isSelected}
                onClick={(e) => handleSelect(index, e)}
                ref={(node) => {
                  if (node && justAddedId === index) {
                    node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    setJustAddedId(null);
                  }
                }}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  fontSize: '0.85rem',
                  '&.Mui-selected': {
                    bgcolor: 'primary.50',
                    fontWeight: 600,
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.100',
                    },
                  },
                }}
              >
                {optLabel}
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
};

export default LeadSingleSelectCell;
